import {
  AlertIcon,
  CrossIcon,
  CurecallList,
  CurecallListHeaders,
  CurecallPaginationBar,
  CurecallTooltip,
  DialogueBubbleIcon,
  PenIcon,
  RoundInfoIcon,
  TrashIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { THRContact } from 'src/types/hr'
import { formatPhone } from 'src/utils/phone.util'

const PAGE_SIZE = 50

type TContactHeaderName =
  | 'ExternalId'
  | 'Lastname'
  | 'Firstname'
  | 'Role'
  | 'ServiceUnit'
  | 'Phone'
  | 'Actions'
type TContactRow = { [key in TContactHeaderName]: ReactNode }
type TSortState = { key: TContactHeaderName; descOrder: boolean }

const getSortValue = (contact: THRContact, key: TContactHeaderName): string => {
  switch (key) {
    case 'ExternalId':
      return contact.remoteId ?? ''
    case 'Lastname':
      return contact.lastname ?? ''
    case 'Firstname':
      return contact.firstname ?? ''
    case 'Role':
      return contact.role ?? ''
    case 'ServiceUnit':
      return contact.serviceUnit ?? ''
    default:
      return ''
  }
}

export const ContactsTable = ({
  contacts,
  search,
  onEdit,
  onDelete,
  onSmsLogs,
  onOverrideChannel,
}: Readonly<{
  contacts: THRContact[]
  search: string
  onEdit: (contact: THRContact) => void
  onDelete: (contact: THRContact) => void
  onSmsLogs: (contact: THRContact) => void
  onOverrideChannel?: (contact: THRContact) => void
}>) => {
  const { t } = useTranslation()
  const [sort, setSort] = useState<TSortState>({ key: 'Lastname', descOrder: false })
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [search])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (query === '') return contacts
    return contacts.filter(
      (c) =>
        c.firstname?.toLowerCase().includes(query) ||
        c.lastname?.toLowerCase().includes(query) ||
        c.remoteId?.toLowerCase().includes(query) ||
        c.role?.toLowerCase().includes(query) ||
        c.serviceUnit?.toLowerCase().includes(query),
    )
  }, [contacts, search])

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const cmp = getSortValue(a, sort.key).localeCompare(getSortValue(b, sort.key), undefined, {
          sensitivity: 'base',
        })
        return sort.descOrder ? -cmp : cmp
      }),
    [filtered, sort],
  )

  const handleSort = (key: TContactHeaderName) => {
    setSort((prev) => (prev.key === key ? { key, descOrder: !prev.descOrder } : { key, descOrder: false }))
    setCurrentPage(1)
  }

  const pageContacts = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const pageRows = pageContacts.map((contact) => ({
    ExternalId: <span className="c-font-mono c-text-xs c-text-grey-500">{contact.remoteId ?? '—'}</span>,
    Lastname: contact.lastname ?? '—',
    Firstname: contact.firstname ?? '—',
    Role: <span className="c-text-grey-500">{contact.role ?? '—'}</span>,
    ServiceUnit: <span className="c-text-grey-500">{contact.serviceUnit ?? '—'}</span>,
    Phone: (
      <span className="c-flex c-items-center c-gap-1">
        {formatPhone(contact.phone) || contact.phone}
        {!contact.isPhoneValid && (
          <CurecallTooltip
            content={t('pages.hr.contacts.table.invalid-phone-tooltip')}
            tooltipPosition="right"
            wrapperClassName="c-flex"
          >
            <AlertIcon className="c-h-4 c-w-4 c-text-red-500 c-shrink-0" />
          </CurecallTooltip>
        )}
        {onOverrideChannel && (
          <CurecallTooltip
            content={t('components.override-channel-modal.override-button-label')}
            tooltipPosition="right"
            wrapperClassName="c-flex"
          >
            <div onClick={() => onOverrideChannel(contact)} className="c-cursor-pointer">
              {contact.CommunicationChannelId !== null ? (
                <RoundInfoIcon className="c-h-4 c-w-4 c-text-grey-500" />
              ) : (
                <CrossIcon className="c-h-4 c-w-4 c-text-red-400" />
              )}
            </div>
          </CurecallTooltip>
        )}
      </span>
    ),
    Actions: (
      <div className="c-flex c-items-center c-gap-1">
        <button
          onClick={() => onSmsLogs(contact)}
          className="c-p-1 c-rounded hover:c-bg-grey-100 c-transition-colors"
          aria-label={t('pages.hr.contacts.table.sms-logs')}
        >
          <DialogueBubbleIcon className="c-w-4 c-h-4 c-text-grey-500" />
        </button>
        <button
          onClick={() => onEdit(contact)}
          className="c-p-1 c-rounded hover:c-bg-grey-100 c-transition-colors"
          aria-label={t('common.actions.edit')}
        >
          <PenIcon className="c-w-4 c-h-4 c-text-grey-500" />
        </button>
        <button
          onClick={() => onDelete(contact)}
          className="c-p-1 c-rounded hover:c-bg-red-50 c-transition-colors"
          aria-label={t('common.actions.delete')}
        >
          <TrashIcon className="c-w-4 c-h-4 c-text-red-400" />
        </button>
      </div>
    ),
  }))

  const headers: CurecallListHeaders<TContactRow> = [
    {
      label: t('pages.hr.contacts.table.external-id'),
      key: 'ExternalId',
      isSorted: true,
      onHeaderClick: () => handleSort('ExternalId'),
    },
    {
      label: t('pages.hr.contacts.table.lastname'),
      key: 'Lastname',
      isSorted: true,
      onHeaderClick: () => handleSort('Lastname'),
    },
    {
      label: t('pages.hr.contacts.table.firstname'),
      key: 'Firstname',
      isSorted: true,
      onHeaderClick: () => handleSort('Firstname'),
    },
    {
      label: t('pages.hr.contacts.table.role'),
      key: 'Role',
      isSorted: true,
      onHeaderClick: () => handleSort('Role'),
    },
    {
      label: t('pages.hr.contacts.table.service-unit'),
      key: 'ServiceUnit',
      isSorted: true,
      onHeaderClick: () => handleSort('ServiceUnit'),
    },
    { label: t('pages.hr.contacts.table.phone'), key: 'Phone' },
    { label: '', key: 'Actions' },
  ]

  return (
    <div className="c-flex c-flex-col c-flex-1 c-min-h-0 c-gap-3">
      <div className="c-flex-1 c-min-h-0 c-overflow-y-auto">
        <CurecallList rowsData={pageRows} headersData={headers} currentOrder={sort} />
      </div>
      {sorted.length > PAGE_SIZE && (
        <CurecallPaginationBar
          totalItems={sorted.length}
          itemsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          numberOfVisiblePages={7}
          pageLabel={t('common.actions.page')}
          pagesLabel={t('common.actions.pages')}
          onClick={(page) => setCurrentPage(page)}
        />
      )}
    </div>
  )
}
